<script setup lang="ts">
import mitt from "@/application/plugins/mitt";
import { Snackbar, SnackbarAction } from "@/application/types";
import { computed, ref } from "vue";
import { type RouteLocationNamedRaw, useRouter } from "vue-router";

const router = useRouter();

const data = ref<Snackbar | null>(null);
const isShowing = ref(false);

mitt.on("snackbar", (payload) => {
  data.value = payload;
  isShowing.value = true;
});

const containsHTML = computed(() =>
  data.value ? /<[a-z][\s\S]*>/i.test(data.value.text) : false,
);

const closeSnackbar = () => {
  isShowing.value = false;
};

const followRoute = (route: RouteLocationNamedRaw) => {
  router.push(route);
  closeSnackbar();
};

const executeAction = (action: SnackbarAction) => {
  action.action();
  closeSnackbar();
};
</script>

<template>
  <v-snackbar
    v-if="data"
    v-model="isShowing"
    :color="data.color"
    :timeout="data.persistent ? -1 : undefined"
    :location="data.location ?? undefined"
  >
    <span v-if="containsHTML" v-html="data.text"></span>
    <template v-else>
      {{ data.text }}
    </template>

    <template #actions>
      <VBtn
        v-if="data.route"
        size="small"
        variant="plain"
        prepend-icon="$visible"
        :text="$t('actions.view')"
        @click="followRoute(data.route)"
      />
      <VBtn
        v-if="data.undoAction"
        size="small"
        variant="plain"
        prepend-icon="$undo"
        :text="$t('actions.restore')"
        @click="data.undoAction"
      />
      <VBtn
        v-if="data.persistent && !data.actions"
        size="small"
        variant="plain"
        icon="$close"
        @click="closeSnackbar"
      />
      <VBtn
        v-for="(action, index) in data.actions"
        v-else
        :key="index"
        size="small"
        variant="plain"
        :prepend-icon="action.icon"
        :text="action.text"
        @click="() => executeAction(action)"
      />
    </template>
  </v-snackbar>
</template>
